import SignatureComponent from '@/components/Common/SignatureComponent/SignatureComponent';
import Checkbox from '@/components/shared/Checkbox';
import { useClaimUpload } from '@/context/ClaimUploadContext';
import { IDetailsItem, TCheckbox } from '@/types';
import clsx from 'clsx';
import { useFormContext } from 'react-hook-form';
import Divider from 'ui/ds/Divider';
import UploadFormSection from '../UploadFormSection';
import { Button } from 'ui/mobile-ds';

const UserDetails = ({ iconUrl, title, subtitle }: IDetailsItem) => {
  return (
    <>
      <div className="flex gap-3 items-center border-mobile-gray-100 border-[1px] p-4 md:p-0 md:border-none rounded-2xl md:rounded-none text-left md:text-center">
        <img src={iconUrl} className="w-11 h-11 my-[6px] flex-shrink-0" />
        <div>
          <p className="text-gray-600 text-left text-base font-semibold">
            {title}
          </p>
          <p className="text-gray-400 font-medium text-sm">{subtitle}</p>
        </div>
      </div>
      <Divider className="hidden md:block" />
    </>
  );
};

const ClaimDetails = ({ title, subtitle }: IDetailsItem) => {
  return (
    <div className="flex flex-col gap-2 min-w-0">
      <p className="text-gray-400 font-medium text-sm">{title}</p>
      <p className="text-gray-600 text-base font-semibold break-words [overflow-wrap:anywhere]">
        {subtitle}
      </p>
    </div>
  );
};

const AccountDetails = ({ iconUrl, title, subtitle }: IDetailsItem) => {
  return (
    <div className="mt-4 md:mt-0 border-mobile-gray-100 border-[1px] p-4 md:p-0 md:border-none rounded-2xl md:rounded-none text-left md:text-center flex gap-4 items-center">
      <img src={iconUrl} className="w-8 h-8" />
      <div className="flex flex-col">
        <p className="font-medium text-mobile-gray-600 leading-[150%] text-left">
          {title}
        </p>
        <p className="font-medium text-mobile-gray-500 leading-[150%] text-sm">
          {subtitle}
        </p>
      </div>
    </div>
  );
};

const ReviewAndSign = () => {
  const { register, watch } = useFormContext();
  const {
    activeSteps,
    claimDetails,
    state: { isSubmitting },
  } = useClaimUpload();
  const { currentStep } = activeSteps;

  const checkboxes = currentStep?.checkboxes;

  return (
    <div
      className={clsx({
        'mt-10 flex flex-col items-center gap-5 w-full max-w-[620px] mx-auto':
          true,
      })}
    >
      <UploadFormSection className="w-full">
        {currentStep?.claimDetailsForUI?.map((item) => {
          if (item.type === 'user-details') {
            return (
              <>
                {item.detailsList?.map((item) => (
                  <UserDetails
                    title={item.title}
                    subtitle={item.subtitle}
                    iconUrl={item.iconUrl}
                  />
                ))}
              </>
            );
          }
          if (item.type === 'claim-details') {
            return (
              <>
                <div className="border-mobile-gray-100 mt-4 md:mt-0 border md:p-0 md:border-none p-4 rounded-2xl text-left md:text-center flex flex-col md:grid md:grid-flow-col md:auto-cols-[minmax(0,max-content)] gap-2 justify-between">
                  {item.detailsList?.map((value, index) => (
                    <div className="flex flex-col min-w-0">
                      <ClaimDetails
                        title={value.title}
                        subtitle={value.subtitle}
                      />
                      {index < item.detailsList.length - 1 && (
                        <div className="my-4 mb:my-0 md:hidden">
                          <Divider className="border-dashed border-mobile-gray-200" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            );
          }
          if (item.type === 'account-details') {
            return (
              <>
                <Divider className="hidden md:block" />
                {item.detailsList?.map((item) => (
                  <AccountDetails
                    title={item.title}
                    subtitle={item.subtitle}
                    iconUrl={item.iconUrl}
                  />
                ))}
              </>
            );
          }
        })}
      </UploadFormSection>
      <div className="flex flex-col md:items-center items-start gap-3 w-[90%]">
        {checkboxes?.map((checkbox: TCheckbox) => {
          const { text, name } = checkbox;
          const isSelected = watch(name);
          return (
            <Checkbox
              title={text}
              selected={isSelected}
              {...register(name, {
                required: 'Please complete all declarations to proceed',
              })}
            />
          );
        })}
      </div>
      <div className="flex gap-3 w-[90%] justify-center md:justify-start">
        <SignatureComponent
          className="w-full items-center sm:items-stretch"
          memberName={claimDetails?.memberName}
          selected={watch('signature')}
          {...register('signature', {
            required: 'Please finish signing to proceed',
          })}
        >
          <Button
            type="submit"
            className="hidden sm:flex ml-auto max-w-[130px] w-full text-base"
            loading={isSubmitting}
          >
            Submit
          </Button>
        </SignatureComponent>
      </div>
    </div>
  );
};

export default ReviewAndSign;
